import React, { useState } from "react";
import styled from "styled-components";
import axios from "axios";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import AIChatInterface from "./components/AIChatInterface";
import AzureAIChatInterface from "./components/AzureAIChatInterface";
import SajuTester from "./components/SajuTester";
import ClientPage from "./pages/ClientPage";
import AdminPage from "./pages/AdminPage";
import ReportPage from "./pages/ReportPage";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import PaymentFailPage from "./pages/PaymentFailPage";
import LegalPage from "./pages/LegalPage";
import MainPage from "./pages/MainPage";
import VisitPage from "./pages/VisitPage";

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
  font-family: "Noto Sans KR", sans-serif;
`;

const Title = styled.h1`
  text-align: center;
  color: #2c3e50;
  margin-bottom: 30px;
  font-size: 2.5rem;
`;

const Form = styled.form`
  background: white;
  padding: 30px;
  border-radius: 15px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
  margin-bottom: 30px;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: #34495e;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  border: 2px solid #e1e8ed;
  border-radius: 8px;
  font-size: 16px;
  transition: border-color 0.3s;

  &:focus {
    outline: none;
    border-color: #3498db;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 12px;
  border: 2px solid #e1e8ed;
  border-radius: 8px;
  font-size: 16px;
  background-color: white;

  &:focus {
    outline: none;
    border-color: #3498db;
  }
`;

const Button = styled.button`
  width: 100%;
  padding: 15px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.2s;

  &:hover {
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const AIButton = styled.button`
  background: linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%);
  color: white;
  border: none;
  padding: 15px 30px;
  border-radius: 25px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  margin: 10px;
  display: inline-block;
  transition: all 0.3s;
  box-shadow: 0 4px 15px rgba(255, 107, 107, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(255, 107, 107, 0.4);
  }

  &:active {
    transform: translateY(0);
  }
`;

const AzureButton = styled.button`
  background: linear-gradient(135deg, #0078d4 0%, #106ebe 100%);
  color: white;
  border: none;
  padding: 15px 30px;
  border-radius: 25px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  margin: 10px;
  display: inline-block;
  transition: all 0.3s;
  box-shadow: 0 4px 15px rgba(0, 120, 212, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 120, 212, 0.4);
  }

  &:active {
    transform: translateY(0);
  }
`;

const AIButtonContainer = styled.div`
  text-align: center;
  margin: 20px 0;
`;

const ResultContainer = styled.div`
  background: white;
  padding: 30px;
  border-radius: 15px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
`;

const SajuGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
  margin: 20px 0;
  text-align: center;
`;

const SajuPillar = styled.div`
  background: #f8f9fa;
  padding: 15px;
  border-radius: 8px;
  border: 2px solid #e1e8ed;
`;

const PillarTitle = styled.div`
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 10px;
`;

const Character = styled.div`
  font-size: 24px;
  font-weight: bold;
  margin: 5px 0;
  color: #e74c3c;
`;

const AnalysisSection = styled.div`
  margin: 30px 0;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 10px;
`;

const SectionTitle = styled.h3`
  color: #2c3e50;
  margin-bottom: 15px;
  border-bottom: 2px solid #3498db;
  padding-bottom: 10px;
`;

const WuxingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 15px;
  margin: 20px 0;
`;

const WuxingCard = styled.div<{ $element: string }>`
  background: ${(props) => {
    switch (props.$element) {
      case "목":
        return "linear-gradient(135deg, #2ecc71, #27ae60)";
      case "화":
        return "linear-gradient(135deg, #e74c3c, #c0392b)";
      case "토":
        return "linear-gradient(135deg, #f39c12, #e67e22)";
      case "금":
        return "linear-gradient(135deg, #95a5a6, #7f8c8d)";
      case "수":
        return "linear-gradient(135deg, #3498db, #2980b9)";
      default:
        return "#f8f9fa";
    }
  }};
  color: white;
  padding: 20px;
  border-radius: 10px;
  text-align: center;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.2);
`;

const ElementName = styled.div`
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 10px;
`;

const ElementCount = styled.div`
  font-size: 24px;
  font-weight: bold;
`;

const ExtendedAnalysisContainer = styled.div`
  background: #f8f9fa;
  padding: 25px;
  border-radius: 15px;
  margin: 20px 0;
`;

const BalanceScore = styled.div`
  text-align: center;
  margin: 20px 0;
  padding: 20px;
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const ScoreCircle = styled.div<{ $score: number }>`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: conic-gradient(
    from 0deg,
    #3498db 0deg,
    #3498db ${(props) => props.$score * 3.6}deg,
    #ecf0f1 ${(props) => props.$score * 3.6}deg,
    #ecf0f1 360deg
  );
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 15px;
  font-size: 24px;
  font-weight: bold;
  color: #2c3e50;
`;

const RecommendationGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin: 20px 0;
`;

const RecommendationCard = styled.div`
  background: white;
  padding: 20px;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const RecommendationTitle = styled.h4`
  color: #2c3e50;
  margin-bottom: 15px;
  font-size: 16px;
  border-bottom: 1px solid #ecf0f1;
  padding-bottom: 8px;
`;

const RecommendationList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const RecommendationItem = styled.li`
  padding: 5px 0;
  color: #34495e;

  &:before {
    content: "✓ ";
    color: #27ae60;
    font-weight: bold;
  }
`;

interface FormData {
  year: number | "";
  month: number | "";
  day: number | "";
  hour: number | "";
  gender: string;
  name: string;
}

interface DaeunInfo {
  period: string;
  start_age: number;
  end_age: number;
  gan: string;
  ji: string;
  gan_wuxing: string;
  ji_wuxing: string;
  is_current: boolean;
  fortune_level: string;
  characteristics: string[];
  major_events: string[];
  advice: string;
}

interface DaeunResult {
  basic_info: any;
  palja: {
    year_pillar: { stem: string; branch: string };
    month_pillar: { stem: string; branch: string };
    day_pillar: { stem: string; branch: string };
    hour_pillar: { stem: string; branch: string };
  };
  daeun_analysis: {
    daeun_start_age: number;
    is_forward: boolean;
    current_age: number;
    daeun_list: DaeunInfo[];
  };
}

interface SaeunInfo {
  month: number;
  month_name: string;
  gan: string;
  ji: string;
  gan_wuxing: string;
  ji_wuxing: string;
  fortune_score: number;
  fortune_grade: string;
  interaction_analysis: {
    favorable_aspects: string[];
    challenging_aspects: string[];
    recommendations: string[];
  };
  summary: string;
}

interface SaeunResult {
  basic_info: {
    name: string;
    birth_date: string;
    gender: string;
    target_year: number;
  };
  palja: {
    year_pillar: { stem: string; branch: string };
    month_pillar: { stem: string; branch: string };
    day_pillar: { stem: string; branch: string };
    hour_pillar: { stem: string; branch: string };
  };
  saeun_analysis: {
    target_year: number;
    yearly_saeun: {
      gan: string;
      ji: string;
      gan_wuxing: string;
      ji_wuxing: string;
      ganzhi: string;
    };
    monthly_saeun: SaeunInfo[];
    annual_score: {
      total_score: number;
      normalized_score: number;
      grade: string;
      yearly_contribution: number;
      monthly_average: number;
    };
    saeun_interaction: {
      yearly: {
        score: number;
        fortune_level: string;
        characteristics: string[];
        opportunities: string[];
        warnings: string[];
      };
      monthly: Array<{
        score: number;
        fortune_level: string;
        characteristics: string[];
        opportunities: string[];
        warnings: string[];
        month: number;
        ganzhi: string;
      }>;
    };
    critical_periods: {
      best_months: Array<{ month: number; score: number; level: string }>;
      caution_months: Array<{ month: number; score: number; level: string }>;
      opportunity_months: Array<{ month: number; opportunities: string[] }>;
    };
    summary: string;
  };
}

interface SajuResult {
  basic_info: any;
  saju_palja: {
    year_pillar: { stem: string; branch: string };
    month_pillar: { stem: string; branch: string };
    day_pillar: { stem: string; branch: string };
    hour_pillar: { stem: string; branch: string };
  };
  wuxing_analysis: {
    목: number;
    화: number;
    토: number;
    금: number;
    수: number;
    extended_analysis?: {
      balance_analysis: {
        balance_score: number;
        excessive_elements: string[];
        deficient_elements: string[];
        dominant_element: string;
        weakest_element: string;
      };
      personality_analysis: {
        personality_type: string;
        strengths: string[];
        weaknesses: string[];
        dominant_traits: string[];
        advice: string;
      };
      recommendations: {
        colors: string[];
        directions: string[];
        lifestyle: string[];
        foods: string[];
        activities: string[];
        career_advice: string[];
        health_advice: string[];
        relationship_advice: string[];
      };
      wuxing_details: {
        [element: string]: {
          count: number;
          percentage: number;
          strength: string;
          meaning: string;
          characteristics: string[];
        };
      };
    };
  };
  interpretations: {
    personality: string;
    career: string;
    health: string;
    relationships: string;
    wealth: string;
  };
}

function App() {
  const [formData, setFormData] = useState<FormData>({
    year: "",
    month: "",
    day: "",
    hour: "",
    gender: "male",
    name: "",
  });

  const [result, setResult] = useState<SajuResult | null>(null);
  const [daeunResult, setDaeunResult] = useState<DaeunResult | null>(null);
  const [saeunResult, setSaeunResult] = useState<SaeunResult | null>(null);
  const [loveFortuneResult, setLoveFortuneResult] = useState<any>(null);
  const [extendedFortuneResult, setExtendedFortuneResult] = useState<any>(null);
  const [phase2FortuneResult, setPhase2FortuneResult] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [daeunLoading, setDaeunLoading] = useState(false);
  const [saeunLoading, setSaeunLoading] = useState(false);
  const [targetYear, setTargetYear] = useState<number>(
    new Date().getFullYear()
  );
  const [error, setError] = useState("");
  const [showAIChat, setShowAIChat] = useState(false);
  const [showAzureChat, setShowAzureChat] = useState(false);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]:
        name === "year" || name === "month" || name === "day" || name === "hour"
          ? value === ""
            ? ""
            : parseInt(value)
          : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setDaeunLoading(true);
    setSaeunLoading(true);
    setError("");

    try {
      console.log("종합 분석 요청 데이터:", formData);
      
      // 1. 기본 사주 분석
      const sajuResponse = await axios.post(
        "http://localhost:8000/api/v1/saju/analyze",
        formData
      );
      console.log("사주 분석 완료:", sajuResponse.data);
      setResult(sajuResponse.data);

      // 2. 대운 분석
      const daeunResponse = await axios.post(
        "http://localhost:8000/api/v1/saju/daeun",
        formData
      );
      console.log("대운 분석 완료:", daeunResponse.data);
      setDaeunResult(daeunResponse.data);

      // 3. 세운 분석
      const saeunResponse = await axios.post(
        `http://localhost:8000/api/v1/saju/saeun?target_year=${targetYear}`,
        formData
      );
      console.log("세운 분석 완료:", saeunResponse.data);
      setSaeunResult(saeunResponse.data);

      // 4. 연애운 분석 (임시 데이터로 대체)
      try {
        const loveFortuneResponse = await axios.post(
          "http://localhost:8000/api/v1/saju/love-fortune",
          formData
        );
        console.log("연애운 분석 완료:", loveFortuneResponse.data);
        setLoveFortuneResult(loveFortuneResponse.data);
      } catch (loveError) {
        console.warn("연애운 API 호출 실패, 임시 데이터 사용:", loveError);
        // 임시 연애운 데이터
        const tempLoveData = {
          success: true,
          data: {
            basic_info: {
              name: formData.name,
              birth_date: `${formData.year}년 ${formData.month}월 ${formData.day}일 ${formData.hour}시`,
              gender: formData.gender
            },
            love_fortune_analysis: {
              ideal_type: {
                description: "활동적이고 밝은 성격의 사람을 선호합니다. 리더십이 있고 진취적인 상대에게 매력을 느낍니다.",
                key_traits: ["매력적인 외모", "좋은 성격", "가치관 일치"]
              },
              love_style: {
                description: "직접적이고 적극적인 어프로치를 선호합니다. 솔직하게 마음을 표현하는 스타일입니다.",
                approach: "진심 어린 마음으로 다가가세요",
                strengths: ["진실한 마음", "깊은 애정"],
                advice: "자신만의 매력을 발휘하여 좋은 인연을 만들어보세요."
              },
              marriage_timing: {
                early: 25,
                ideal: 29,
                late: 35
              },
              monthly_fortune: {
                best_months: ["5월", "8월", "10월"],
                caution_months: ["2월", "7월"],
                advice: "긍정적인 마음으로 새로운 만남에 열려있으세요."
              }
            }
          }
        };
        setLoveFortuneResult(tempLoveData);
      }

      // 5. 확장 운세 분석 (1단계: 4개 운세)
      try {
        const extendedFortuneResponse = await axios.post(
          "http://localhost:8000/api/v1/saju/extended-fortune",
          formData
        );
        console.log("확장 운세 분석 완료:", extendedFortuneResponse.data);
        setExtendedFortuneResult(extendedFortuneResponse.data);
      } catch (extendedError) {
        console.warn("확장 운세 API 호출 실패, 임시 데이터 사용:", extendedError);
        // 임시 확장 운세 데이터
        const tempExtendedData = {
          success: true,
          data: {
            basic_info: {
              name: formData.name,
              birth_date: `${formData.year}년 ${formData.month}월 ${formData.day}일 ${formData.hour}시`,
              gender: formData.gender
            },
            residence_fortune: {
              moving_direction: "동쪽 방향으로 이사하면 성장과 발전에 도움이 됩니다",
              avoid_direction: "서쪽 방향은 피하는 것이 좋습니다",
              house_type: "아파트보다는 단독주택이나 빌라가 더 좋습니다",
              house_reason: "자연과 가까운 환경에서 에너지를 얻습니다",
              interior_colors: ["초록색", "갈색", "베이지색"],
              room_layout: {
                "침실": "동쪽 방향 침실이 숙면에 도움됩니다",
                "거실": "가족이 모이는 공간은 밝고 넓게 꾸미세요",
                "부엌": "화기가 있는 부엌은 남동쪽이 길합니다",
                "서재": "집중이 필요한 공간은 조용한 북쪽이나 동쪽이 좋습니다"
              },
              best_moving_months: ["3월", "4월", "5월"],
              feng_shui_tips: [
                "목 기운을 살리는 초록색, 갈색 계열 인테리어를 하세요",
                "현관은 항상 깨끗하게 유지하고 밝은 조명을 설치하세요",
                "침실에는 거울을 두지 마세요",
                "화장실 문은 항상 닫아두세요",
                "식물이나 꽃을 키워 생기를 더하세요"
              ]
            },
            transportation_fortune: {
              car_colors: ["초록색", "갈색", "베이지색"],
              license_numbers: "번호판 끝자리는 3, 8번이 길합니다",
              accident_risk_months: ["7월", "8월"],
              transport_preference: "대중교통 이용이 더 안전하고 효율적입니다",
              transport_reason: "환경을 생각하고 스트레스를 줄일 수 있습니다",
              travel_directions: ["동쪽", "남쪽"],
              driving_tips: [
                "7월, 8월에는 특히 안전운전에 주의하세요",
                "빗길이나 눈길에서는 더욱 신중하게 운전하세요",
                "장거리 운전 전에는 충분한 휴식을 취하세요"
              ]
            },
            social_fortune: {
              sns_active_months: ["5월", "6월", "8월", "9월"],
              activity_reason: "활발하고 외향적인 성향으로 소셜 활동이 잘 맞습니다",
              profile_colors: ["초록색", "갈색", "베이지색"],
              communication_style: "성장 지향적이고 긍정적인 메시지를 전달하세요",
              online_timing: "오전~오후 시간대 활동이 효과적입니다",
              social_strategies: [
                "성장과 발전 관련 콘텐츠 공유",
                "자연과 환경 관련 주제",
                "학습과 자기계발 정보"
              ]
            },
            hobby_fortune: {
              art_fields: ["문학", "서예", "원예", "목공예"],
              sports_type: "단체 운동이 더 적합합니다",
              sports_examples: ["축구", "농구", "배구", "테니스"],
              sports_reason: "활발하고 사교적인 성향으로 팀 스포츠가 잘 맞습니다",
              collection_items: ["책", "식물", "목재 소품", "친환경 제품"],
              creative_activities: ["블로그 작성", "소설 쓰기", "정원 가꾸기"],
              hobby_advice: [
                "다른 사람들과 함께 할 수 있는 취미를 선택하세요",
                "창의성을 발휘할 수 있는 활동이 좋습니다",
                "새로운 것에 도전하는 것을 두려워하지 마세요"
              ]
            }
          }
        };
        setExtendedFortuneResult(tempExtendedData);
      }

      // 6. Phase 2 확장 운세 분석 (직업운, 건강운, 학업운, 가족운)
      try {
        const phase2FortuneResponse = await axios.post(
          "http://localhost:8000/api/v1/saju/extended-fortune-phase2",
          formData
        );
        console.log("Phase 2 확장 운세 분석 완료:", phase2FortuneResponse.data);
        setPhase2FortuneResult(phase2FortuneResponse.data);
      } catch (phase2Error) {
        console.warn("Phase 2 확장 운세 API 호출 실패, 임시 데이터 사용:", phase2Error);
        // 임시 Phase 2 데이터
        const tempPhase2Data = {
          success: true,
          data: {
            basic_info: {
              name: formData.name,
              birth_date: `${formData.year}년 ${formData.month}월 ${formData.day}일 ${formData.hour}시`,
              gender: formData.gender
            },
            phase2_fortune: {
              career_fortune: {
                job_change_months: ["3월", "6월", "9월"],
                change_reason: "성장과 발전을 추구하는 성향으로 새로운 도전이 좋습니다",
                promotion_advice: "실무 능력을 키우고 인맥을 넓히는 시기입니다",
                startup_fields: ["IT", "컨설팅", "서비스업"],
                side_jobs: ["온라인 강의", "블로그 운영"],
                networking_advice: "다양한 사람들과 네트워킹을 확대하세요"
              },
              health_fortune: {
                organ_care: {
                  "심장": "심혈관 건강을 위해 규칙적인 운동을 하세요",
                  "간담": "간 건강에 주의하고 스트레스 관리가 중요합니다"
                },
                exercise_recommendations: ["유산소 운동", "요가", "수영"],
                diet_advice: ["신선한 채소 섭취", "규칙적인 식사", "충분한 수분"],
                checkup_timing: ["3월", "9월"]
              },
              study_fortune: {
                study_months: ["2월", "3월", "9월", "10월"],
                study_reason: "집중력이 높아지는 시기로 깊이 있는 학습이 가능합니다",
                certifications: ["토익", "컴활", "정보처리기사"],
                reading_genres: ["자기계발서", "경영서", "IT기술서"]
              },
              family_fortune: {
                parent_relationship: "부모님과 깊이 있는 소통을 하는 것이 좋습니다",
                parent_advice: ["정기적으로 안부를 묻기", "효도 실천"],
                children_timing: ["봄", "가을"],
                gathering_activities: ["등산", "문화활동", "여행"]
              }
            }
          }
        };
        setPhase2FortuneResult(tempPhase2Data);
      }

    } catch (err: any) {
      console.error("종합 분석 오류:", err);
      console.error("응답 데이터:", err.response?.data);
      setError(err.response?.data?.detail || "분석 중 오류가 발생했습니다.");
    } finally {
      setLoading(false);
      setDaeunLoading(false);
      setSaeunLoading(false);
    }
  };

  const handleDaeunAnalysis = async () => {
    setDaeunLoading(true);
    setError("");

    try {
      console.log("대운 분석 요청 데이터:", formData);
      const response = await axios.post(
        "http://localhost:8000/api/v1/saju/daeun",
        formData
      );
      console.log("대운 분석 API 응답:", response.data);
      setDaeunResult(response.data);
    } catch (err: any) {
      console.error("대운 분석 API 오류:", err);
      console.error("응답 데이터:", err.response?.data);
      setError(
        err.response?.data?.detail || "대운 분석 중 오류가 발생했습니다."
      );
    } finally {
      setDaeunLoading(false);
    }
  };

  const handleSaeunAnalysis = async () => {
    setSaeunLoading(true);
    setError("");

    try {
      console.log("세운 분석 요청 데이터:", formData, "대상연도:", targetYear);
      const response = await axios.post(
        `http://localhost:8000/api/v1/saju/saeun?target_year=${targetYear}`,
        formData
      );
      console.log("세운 분석 API 응답:", response.data);
      setSaeunResult(response.data);
    } catch (err: any) {
      console.error("세운 분석 API 오류:", err);
      console.error("응답 데이터:", err.response?.data);
      setError(
        err.response?.data?.detail || "세운 분석 중 오류가 발생했습니다."
      );
    } finally {
      setSaeunLoading(false);
    }
  };

  return (
    <Container>
      <Title>🔮 사주팔자 분석 서비스</Title>

      <Form onSubmit={handleSubmit}>
        <FormGroup>
          <Label>이름</Label>
          <Input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            placeholder="이름을 입력하세요"
            required
          />
        </FormGroup>

        <FormGroup>
          <Label>생년월일</Label>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gap: "10px",
            }}
          >
            <Input
              type="number"
              name="year"
              value={formData.year}
              onChange={handleInputChange}
              placeholder="년 (예: 1990)"
              min="1900"
              max="2100"
              required
            />
            <Input
              type="number"
              name="month"
              value={formData.month}
              onChange={handleInputChange}
              placeholder="월 (1-12)"
              min="1"
              max="12"
              required
            />
            <Input
              type="number"
              name="day"
              value={formData.day}
              onChange={handleInputChange}
              placeholder="일 (1-31)"
              min="1"
              max="31"
              required
            />
          </div>
        </FormGroup>

        <FormGroup>
          <Label>태어난 시간</Label>
          <Select
            name="hour"
            value={formData.hour}
            onChange={handleInputChange}
            required
          >
            <option value="">시간을 선택하세요</option>
            {Array.from({ length: 24 }, (_, i) => (
              <option key={i} value={i}>
                {i}시
              </option>
            ))}
          </Select>
        </FormGroup>

        <FormGroup>
          <Label>성별</Label>
          <Select
            name="gender"
            value={formData.gender}
            onChange={handleInputChange}
          >
            <option value="male">남성</option>
            <option value="female">여성</option>
          </Select>
        </FormGroup>

        <Button type="submit" disabled={loading}>
          {loading ? "종합 분석 중... (사주 + 대운 + 세운 + 연애운)" : "🔮 종합 사주 분석하기 (사주 + 대운 + 세운 + 연애운)"}
        </Button>
      </Form>

      {/* 대운 분석 섹션 - 통합 분석으로 인해 비활성화
      <Form
        style={{
          marginTop: "20px",
          background: "#f8f9fa",
          border: "2px solid #e9ecef",
        }}
      >
        <div style={{ textAlign: "center", marginBottom: "20px" }}>
          <h3 style={{ color: "#495057", margin: "0 0 10px 0" }}>
            🌟 대운 분석
          </h3>
          <p style={{ color: "#6c757d", margin: 0, fontSize: "14px" }}>
            인생 전체의 운세 흐름을 10년 주기로 분석합니다
          </p>
        </div>
        <Button
          type="button"
          disabled={daeunLoading || !formData.name}
          onClick={handleDaeunAnalysis}
          style={{
            background: daeunLoading
              ? "#6c757d"
              : "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
            marginTop: "0",
          }}
        >
          {daeunLoading ? "대운 분석 중..." : "대운 분석하기"}
        </Button>
      </Form> */}

      {/* 세운 분석 섹션 - 통합 분석으로 인해 비활성화
      <Form
        style={{
          marginTop: "20px",
          background: "#e8f5e8",
          border: "2px solid #c8e6c9",
        }}
      >
        <div style={{ textAlign: "center", marginBottom: "20px" }}>
          <h3 style={{ color: "#2e7d32", margin: "0 0 10px 0" }}>
            ⏰ 세운 분석
          </h3>
          <p style={{ color: "#4caf50", margin: 0, fontSize: "14px" }}>
            특정 연도의 월별 운세를 상세하게 분석합니다
          </p>
        </div>

        <FormGroup style={{ marginBottom: "20px" }}>
          <Label style={{ color: "#2e7d32" }}>분석 대상 연도</Label>
          <Input
            type="number"
            value={targetYear}
            onChange={(e) =>
              setTargetYear(
                parseInt(e.target.value) || new Date().getFullYear()
              )
            }
            min="2020"
            max="2030"
            style={{ border: "2px solid #c8e6c9" }}
          />
        </FormGroup>

        <Button
          type="button"
          disabled={saeunLoading || !formData.name}
          onClick={handleSaeunAnalysis}
          style={{
            background: saeunLoading
              ? "#6c757d"
              : "linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)",
            marginTop: "0",
          }}
        >
          {saeunLoading ? "세운 분석 중..." : `${targetYear}년 세운 분석하기`}
        </Button>
      </Form> */}

      {error && (
        <div
          style={{
            padding: "20px",
            background: "#fee",
            color: "#c33",
            borderRadius: "8px",
            marginBottom: "20px",
          }}
        >
          {error}
        </div>
      )}

      {result && (
        <ResultContainer>
          <h2>{result.basic_info?.name || "사용자"}님의 사주팔자</h2>

          <SajuGrid>
            <SajuPillar>
              <PillarTitle>년주</PillarTitle>
              <Character>{result.saju_palja.year_pillar.stem}</Character>
              <Character>{result.saju_palja.year_pillar.branch}</Character>
            </SajuPillar>
            <SajuPillar>
              <PillarTitle>월주</PillarTitle>
              <Character>{result.saju_palja.month_pillar.stem}</Character>
              <Character>{result.saju_palja.month_pillar.branch}</Character>
            </SajuPillar>
            <SajuPillar>
              <PillarTitle>일주</PillarTitle>
              <Character>{result.saju_palja.day_pillar.stem}</Character>
              <Character>{result.saju_palja.day_pillar.branch}</Character>
            </SajuPillar>
            <SajuPillar>
              <PillarTitle>시주</PillarTitle>
              <Character>{result.saju_palja.hour_pillar.stem}</Character>
              <Character>{result.saju_palja.hour_pillar.branch}</Character>
            </SajuPillar>
          </SajuGrid>

          <AnalysisSection>
            <SectionTitle>� 오행 분석</SectionTitle>

            <WuxingGrid>
              <WuxingCard $element="목">
                <ElementName>목(木)</ElementName>
                <ElementCount>{result.wuxing_analysis.목}</ElementCount>
              </WuxingCard>
              <WuxingCard $element="화">
                <ElementName>화(火)</ElementName>
                <ElementCount>{result.wuxing_analysis.화}</ElementCount>
              </WuxingCard>
              <WuxingCard $element="토">
                <ElementName>토(土)</ElementName>
                <ElementCount>{result.wuxing_analysis.토}</ElementCount>
              </WuxingCard>
              <WuxingCard $element="금">
                <ElementName>금(金)</ElementName>
                <ElementCount>{result.wuxing_analysis.금}</ElementCount>
              </WuxingCard>
              <WuxingCard $element="수">
                <ElementName>수(水)</ElementName>
                <ElementCount>{result.wuxing_analysis.수}</ElementCount>
              </WuxingCard>
            </WuxingGrid>

            {result.wuxing_analysis.extended_analysis && (
              <ExtendedAnalysisContainer>
                <h4
                  style={{
                    color: "#2c3e50",
                    textAlign: "center",
                    marginBottom: "20px",
                  }}
                >
                  🔮 상세 오행 분석
                </h4>

                <BalanceScore>
                  <h5 style={{ color: "#2c3e50", marginBottom: "15px" }}>
                    오행 균형 점수
                  </h5>
                  <ScoreCircle
                    $score={
                      result.wuxing_analysis.extended_analysis.balance_analysis
                        .balance_score
                    }
                  >
                    {
                      result.wuxing_analysis.extended_analysis.balance_analysis
                        .balance_score
                    }
                    점
                  </ScoreCircle>
                  <p style={{ color: "#7f8c8d", margin: 0 }}>
                    성격 유형:{" "}
                    <strong>
                      {
                        result.wuxing_analysis.extended_analysis
                          .personality_analysis.personality_type
                      }
                    </strong>
                  </p>
                </BalanceScore>

                <div style={{ marginTop: "20px" }}>
                  <SectionTitle>💫 오행별 상세 분석</SectionTitle>
                  {result.wuxing_analysis.extended_analysis
                    .balance_analysis && (
                    <div>
                      <p>
                        <strong>주도적 오행:</strong>{" "}
                        {
                          result.wuxing_analysis.extended_analysis
                            .balance_analysis.dominant_element
                        }
                      </p>
                      <p>
                        <strong>부족한 오행:</strong>{" "}
                        {
                          result.wuxing_analysis.extended_analysis
                            .balance_analysis.weakest_element
                        }
                      </p>

                      {result.wuxing_analysis.extended_analysis
                        .wuxing_details && (
                        <div style={{ marginTop: "20px" }}>
                          {Object.entries(
                            result.wuxing_analysis.extended_analysis
                              .wuxing_details
                          ).map(([element, data]: [string, any]) => (
                            <div
                              key={element}
                              style={{
                                background: "white",
                                padding: "15px",
                                margin: "10px 0",
                                borderRadius: "8px",
                                borderLeft: "4px solid #3498db",
                              }}
                            >
                              <strong>
                                {element}: {data.count}개 ({data.percentage}%)
                              </strong>
                              <p
                                style={{
                                  margin: "5px 0 0 0",
                                  color: "#7f8c8d",
                                }}
                              >
                                강도: {data.strength} - {data.meaning}
                              </p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div style={{ marginTop: "20px" }}>
                  <SectionTitle>✨ 강점 & 약점</SectionTitle>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "20px",
                    }}
                  >
                    <div>
                      <h5 style={{ color: "#27ae60" }}>강점</h5>
                      <ul style={{ listStyle: "none", padding: 0 }}>
                        {result.wuxing_analysis.extended_analysis.personality_analysis?.strengths?.map(
                          (strength: string, index: number) => (
                            <li
                              key={index}
                              style={{ padding: "5px 0", color: "#34495e" }}
                            >
                              ✓ {strength}
                            </li>
                          )
                        )}
                      </ul>
                    </div>
                    <div>
                      <h5 style={{ color: "#e74c3c" }}>개선점</h5>
                      <ul style={{ listStyle: "none", padding: 0 }}>
                        {result.wuxing_analysis.extended_analysis.personality_analysis?.weaknesses?.map(
                          (weakness: string, index: number) => (
                            <li
                              key={index}
                              style={{ padding: "5px 0", color: "#34495e" }}
                            >
                              ⚠ {weakness}
                            </li>
                          )
                        )}
                      </ul>
                    </div>
                  </div>
                </div>

                <div style={{ marginTop: "20px" }}>
                  <SectionTitle>🎯 맞춤형 조언</SectionTitle>
                  <RecommendationGrid>
                    <RecommendationCard>
                      <RecommendationTitle>🎨 추천 색상</RecommendationTitle>
                      <RecommendationList>
                        {result.wuxing_analysis.extended_analysis.recommendations?.colors?.map(
                          (color: string, index: number) => (
                            <RecommendationItem key={index}>
                              {color}
                            </RecommendationItem>
                          )
                        )}
                      </RecommendationList>
                    </RecommendationCard>

                    <RecommendationCard>
                      <RecommendationTitle>🧭 유리한 방향</RecommendationTitle>
                      <RecommendationList>
                        {result.wuxing_analysis.extended_analysis.recommendations?.directions?.map(
                          (direction: string, index: number) => (
                            <RecommendationItem key={index}>
                              {direction}
                            </RecommendationItem>
                          )
                        )}
                      </RecommendationList>
                    </RecommendationCard>

                    <RecommendationCard>
                      <RecommendationTitle>🏃‍♀️ 생활습관</RecommendationTitle>
                      <RecommendationList>
                        {result.wuxing_analysis.extended_analysis.recommendations?.lifestyle?.map(
                          (lifestyle: string, index: number) => (
                            <RecommendationItem key={index}>
                              {lifestyle}
                            </RecommendationItem>
                          )
                        )}
                      </RecommendationList>
                    </RecommendationCard>

                    <RecommendationCard>
                      <RecommendationTitle>💼 직업 조언</RecommendationTitle>
                      <RecommendationList>
                        {result.wuxing_analysis.extended_analysis.recommendations?.career_advice?.map(
                          (advice: string, index: number) => (
                            <RecommendationItem key={index}>
                              {advice}
                            </RecommendationItem>
                          )
                        )}
                      </RecommendationList>
                    </RecommendationCard>

                    <RecommendationCard>
                      <RecommendationTitle>💊 건강 관리</RecommendationTitle>
                      <RecommendationList>
                        {result.wuxing_analysis.extended_analysis.recommendations?.health_advice?.map(
                          (advice: string, index: number) => (
                            <RecommendationItem key={index}>
                              {advice}
                            </RecommendationItem>
                          )
                        )}
                      </RecommendationList>
                    </RecommendationCard>

                    <RecommendationCard>
                      <RecommendationTitle>💑 인간관계</RecommendationTitle>
                      <RecommendationList>
                        {result.wuxing_analysis.extended_analysis.recommendations?.relationship_advice?.map(
                          (advice: string, index: number) => (
                            <RecommendationItem key={index}>
                              {advice}
                            </RecommendationItem>
                          )
                        )}
                      </RecommendationList>
                    </RecommendationCard>
                  </RecommendationGrid>
                </div>
              </ExtendedAnalysisContainer>
            )}
          </AnalysisSection>

          <AnalysisSection>
            <SectionTitle>🌟 성격 분석</SectionTitle>
            <p>{result.interpretations.personality}</p>
          </AnalysisSection>

          <AnalysisSection>
            <SectionTitle>💪 건강운</SectionTitle>
            <p>{result.interpretations.health}</p>
          </AnalysisSection>

          <AnalysisSection>
            <SectionTitle>👥 대인관계</SectionTitle>
            <p>{result.interpretations.relationships}</p>
          </AnalysisSection>

          <AnalysisSection>
            <SectionTitle>💰 재물운</SectionTitle>
            <p>{result.interpretations.wealth}</p>
          </AnalysisSection>

          {/* 연애운 분석 결과 */}
          {loveFortuneResult && (
            <AnalysisSection>
              <SectionTitle>💕 연애운 분석</SectionTitle>
              
              {loveFortuneResult.success ? (
                <div>
                  {loveFortuneResult.data.love_fortune_analysis && (
                    <div>
                      {/* 이상형 분석 */}
                      <div style={{ 
                        background: "#fff5f5", 
                        padding: "20px", 
                        borderRadius: "12px", 
                        margin: "15px 0",
                        border: "1px solid #fed7e2"
                      }}>
                        <h4 style={{ color: "#e53e3e", marginBottom: "15px" }}>
                          💖 이상형 분석
                        </h4>
                        <p style={{ 
                          fontSize: "16px", 
                          lineHeight: "1.6", 
                          color: "#495057",
                          marginBottom: "12px"
                        }}>
                          {loveFortuneResult.data.love_fortune_analysis.ideal_type?.description}
                        </p>
                        {loveFortuneResult.data.love_fortune_analysis.ideal_type?.key_traits && (
                          <div>
                            <strong style={{ color: "#e53e3e" }}>주요 특징:</strong>
                            <div style={{ marginTop: "8px", display: "flex", gap: "8px", flexWrap: "wrap" }}>
                              {loveFortuneResult.data.love_fortune_analysis.ideal_type.key_traits.map((trait: string, i: number) => (
                                <span key={i} style={{
                                  background: "#e53e3e",
                                  color: "white",
                                  padding: "4px 12px",
                                  borderRadius: "15px",
                                  fontSize: "14px"
                                }}>
                                  {trait}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* 연애 스타일 */}
                      <div style={{ 
                        background: "#f0fff4", 
                        padding: "20px", 
                        borderRadius: "12px", 
                        margin: "15px 0",
                        border: "1px solid #c6f6d5"
                      }}>
                        <h4 style={{ color: "#38a169", marginBottom: "15px" }}>
                          💫 연애 스타일
                        </h4>
                        <p style={{ 
                          fontSize: "16px", 
                          lineHeight: "1.6", 
                          color: "#495057",
                          marginBottom: "12px"
                        }}>
                          {loveFortuneResult.data.love_fortune_analysis.love_style?.description}
                        </p>
                        <div style={{ marginTop: "15px" }}>
                          <strong style={{ color: "#38a169" }}>조언:</strong>
                          <p style={{ 
                            fontSize: "15px", 
                            color: "#495057", 
                            marginTop: "8px",
                            fontStyle: "italic"
                          }}>
                            {loveFortuneResult.data.love_fortune_analysis.love_style?.advice}
                          </p>
                        </div>
                      </div>

                      {/* 결혼 적령기 */}
                      <div style={{ 
                        background: "#fffaf0", 
                        padding: "20px", 
                        borderRadius: "12px", 
                        margin: "15px 0",
                        border: "1px solid #fbd38d"
                      }}>
                        <h4 style={{ color: "#dd6b20", marginBottom: "15px" }}>
                          💒 결혼 적령기
                        </h4>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "15px" }}>
                          <div style={{ textAlign: "center" }}>
                            <div style={{ color: "#dd6b20", fontWeight: "bold", fontSize: "18px" }}>
                              {loveFortuneResult.data.love_fortune_analysis.marriage_timing?.early}세
                            </div>
                            <div style={{ fontSize: "14px", color: "#666" }}>이른 결혼</div>
                          </div>
                          <div style={{ textAlign: "center" }}>
                            <div style={{ color: "#dd6b20", fontWeight: "bold", fontSize: "18px" }}>
                              {loveFortuneResult.data.love_fortune_analysis.marriage_timing?.ideal}세
                            </div>
                            <div style={{ fontSize: "14px", color: "#666" }}>이상적 시기</div>
                          </div>
                          <div style={{ textAlign: "center" }}>
                            <div style={{ color: "#dd6b20", fontWeight: "bold", fontSize: "18px" }}>
                              {loveFortuneResult.data.love_fortune_analysis.marriage_timing?.late}세
                            </div>
                            <div style={{ fontSize: "14px", color: "#666" }}>늦은 결혼</div>
                          </div>
                        </div>
                      </div>

                      {/* 월별 연애운 */}
                      <div style={{ 
                        background: "#f7fafc", 
                        padding: "20px", 
                        borderRadius: "12px", 
                        margin: "15px 0",
                        border: "1px solid #e2e8f0"
                      }}>
                        <h4 style={{ color: "#4a5568", marginBottom: "15px" }}>
                          📅 월별 연애운
                        </h4>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
                          <div>
                            <h5 style={{ color: "#38a169", marginBottom: "10px" }}>🍀 좋은 달</h5>
                            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                              {loveFortuneResult.data.love_fortune_analysis.monthly_fortune?.best_months?.map((month: string, i: number) => (
                                <span key={i} style={{
                                  background: "#38a169",
                                  color: "white",
                                  padding: "4px 12px",
                                  borderRadius: "15px",
                                  fontSize: "14px"
                                }}>
                                  {month}
                                </span>
                              ))}
                            </div>
                          </div>
                          <div>
                            <h5 style={{ color: "#e53e3e", marginBottom: "10px" }}>⚠️ 주의할 달</h5>
                            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                              {loveFortuneResult.data.love_fortune_analysis.monthly_fortune?.caution_months?.map((month: string, i: number) => (
                                <span key={i} style={{
                                  background: "#e53e3e",
                                  color: "white",
                                  padding: "4px 12px",
                                  borderRadius: "15px",
                                  fontSize: "14px"
                                }}>
                                  {month}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                        <div style={{ marginTop: "15px" }}>
                          <p style={{ 
                            fontSize: "15px", 
                            color: "#495057", 
                            fontStyle: "italic",
                            textAlign: "center"
                          }}>
                            {loveFortuneResult.data.love_fortune_analysis.monthly_fortune?.advice}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ padding: "15px", background: "#fff3cd", borderRadius: "8px" }}>
                  <p style={{ color: "#856404", margin: 0 }}>
                    연애운 분석 중 문제가 발생했습니다. 백엔드 API 연결을 확인해주세요.
                  </p>
                </div>
              )}
            </AnalysisSection>
          )}
        </ResultContainer>
      )}

      {/* 확장 운세 분석 결과 (1단계: 4개 운세) */}
      {extendedFortuneResult && (
        <ResultContainer style={{ marginTop: "30px" }}>
          <SectionTitle
            style={{
              textAlign: "center",
              fontSize: "2rem",
              marginBottom: "30px",
            }}
          >
            🔮 확장 운세 분석
          </SectionTitle>

          {extendedFortuneResult.success ? (
            <div>
              {/* 주거운 분석 */}
              <AnalysisSection style={{ marginBottom: "25px" }}>
                <SectionTitle style={{ 
                  color: "#8B4513", 
                  borderBottom: "3px solid #8B4513", 
                  paddingBottom: "10px" 
                }}>
                  🏠 주거운 분석
                </SectionTitle>
                
                {extendedFortuneResult.data.residence_fortune && (
                  <div style={{ marginTop: "20px" }}>
                    {/* 이사 방향 */}
                    <div style={{ 
                      background: "#f8f4e6", 
                      padding: "20px", 
                      borderRadius: "12px", 
                      margin: "15px 0",
                      border: "1px solid #e6d7b8"
                    }}>
                      <h4 style={{ color: "#8B4513", marginBottom: "15px" }}>
                        🧭 이사 방향
                      </h4>
                      <p style={{ fontSize: "16px", color: "#495057", lineHeight: "1.6" }}>
                        <strong>✅ 추천:</strong> {extendedFortuneResult.data.residence_fortune.moving_direction}
                      </p>
                      <p style={{ fontSize: "16px", color: "#495057", lineHeight: "1.6", marginTop: "10px" }}>
                        <strong>❌ 피해야 할 방향:</strong> {extendedFortuneResult.data.residence_fortune.avoid_direction}
                      </p>
                    </div>

                    {/* 주택 유형 */}
                    <div style={{ 
                      background: "#f8f4e6", 
                      padding: "20px", 
                      borderRadius: "12px", 
                      margin: "15px 0",
                      border: "1px solid #e6d7b8"
                    }}>
                      <h4 style={{ color: "#8B4513", marginBottom: "15px" }}>
                        🏘️ 주택 유형
                      </h4>
                      <p style={{ fontSize: "16px", color: "#495057", lineHeight: "1.6" }}>
                        <strong>{extendedFortuneResult.data.residence_fortune.house_type}</strong>
                      </p>
                      <p style={{ fontSize: "14px", color: "#666", fontStyle: "italic", marginTop: "8px" }}>
                        {extendedFortuneResult.data.residence_fortune.house_reason}
                      </p>
                    </div>

                    {/* 인테리어 색상 */}
                    <div style={{ 
                      background: "#f8f4e6", 
                      padding: "20px", 
                      borderRadius: "12px", 
                      margin: "15px 0",
                      border: "1px solid #e6d7b8"
                    }}>
                      <h4 style={{ color: "#8B4513", marginBottom: "15px" }}>
                        🎨 인테리어 색상
                      </h4>
                      <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                        {extendedFortuneResult.data.residence_fortune.interior_colors?.map((color: string, i: number) => (
                          <span key={i} style={{
                            background: "#8B4513",
                            color: "white",
                            padding: "8px 16px",
                            borderRadius: "20px",
                            fontSize: "14px",
                            fontWeight: "bold"
                          }}>
                            {color}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* 이사 적기 */}
                    <div style={{ 
                      background: "#f8f4e6", 
                      padding: "20px", 
                      borderRadius: "12px", 
                      margin: "15px 0",
                      border: "1px solid #e6d7b8"
                    }}>
                      <h4 style={{ color: "#8B4513", marginBottom: "15px" }}>
                        📅 이사 적기
                      </h4>
                      <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                        {extendedFortuneResult.data.residence_fortune.best_moving_months?.map((month: string, i: number) => (
                          <span key={i} style={{
                            background: "#228B22",
                            color: "white",
                            padding: "6px 14px",
                            borderRadius: "15px",
                            fontSize: "14px"
                          }}>
                            {month}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </AnalysisSection>

              {/* 교통운 분석 */}
              <AnalysisSection style={{ marginBottom: "25px" }}>
                <SectionTitle style={{ 
                  color: "#4682B4", 
                  borderBottom: "3px solid #4682B4", 
                  paddingBottom: "10px" 
                }}>
                  🚗 교통운 분석
                </SectionTitle>
                
                {extendedFortuneResult.data.transportation_fortune && (
                  <div style={{ marginTop: "20px" }}>
                    {/* 차량 색상 */}
                    <div style={{ 
                      background: "#f0f8ff", 
                      padding: "20px", 
                      borderRadius: "12px", 
                      margin: "15px 0",
                      border: "1px solid #b0d4ff"
                    }}>
                      <h4 style={{ color: "#4682B4", marginBottom: "15px" }}>
                        🚙 차량 색상
                      </h4>
                      <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                        {extendedFortuneResult.data.transportation_fortune.car_colors?.map((color: string, i: number) => (
                          <span key={i} style={{
                            background: "#4682B4",
                            color: "white",
                            padding: "8px 16px",
                            borderRadius: "20px",
                            fontSize: "14px",
                            fontWeight: "bold"
                          }}>
                            {color}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* 번호판 길수 */}
                    <div style={{ 
                      background: "#f0f8ff", 
                      padding: "20px", 
                      borderRadius: "12px", 
                      margin: "15px 0",
                      border: "1px solid #b0d4ff"
                    }}>
                      <h4 style={{ color: "#4682B4", marginBottom: "15px" }}>
                        🔢 번호판 길수
                      </h4>
                      <p style={{ fontSize: "16px", color: "#495057", lineHeight: "1.6" }}>
                        {extendedFortuneResult.data.transportation_fortune.license_numbers}
                      </p>
                    </div>

                    {/* 사고 위험 시기 */}
                    <div style={{ 
                      background: "#f0f8ff", 
                      padding: "20px", 
                      borderRadius: "12px", 
                      margin: "15px 0",
                      border: "1px solid #b0d4ff"
                    }}>
                      <h4 style={{ color: "#4682B4", marginBottom: "15px" }}>
                        ⚠️ 주의할 시기
                      </h4>
                      <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                        {extendedFortuneResult.data.transportation_fortune.accident_risk_months?.map((month: string, i: number) => (
                          <span key={i} style={{
                            background: "#dc3545",
                            color: "white",
                            padding: "6px 14px",
                            borderRadius: "15px",
                            fontSize: "14px"
                          }}>
                            {month}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* 교통수단 선호도 */}
                    <div style={{ 
                      background: "#f0f8ff", 
                      padding: "20px", 
                      borderRadius: "12px", 
                      margin: "15px 0",
                      border: "1px solid #b0d4ff"
                    }}>
                      <h4 style={{ color: "#4682B4", marginBottom: "15px" }}>
                        🚌 교통수단 추천
                      </h4>
                      <p style={{ fontSize: "16px", color: "#495057", lineHeight: "1.6" }}>
                        <strong>{extendedFortuneResult.data.transportation_fortune.transport_preference}</strong>
                      </p>
                      <p style={{ fontSize: "14px", color: "#666", fontStyle: "italic", marginTop: "8px" }}>
                        {extendedFortuneResult.data.transportation_fortune.transport_reason}
                      </p>
                    </div>
                  </div>
                )}
              </AnalysisSection>

              {/* 소셜운 분석 */}
              <AnalysisSection style={{ marginBottom: "25px" }}>
                <SectionTitle style={{ 
                  color: "#9370DB", 
                  borderBottom: "3px solid #9370DB", 
                  paddingBottom: "10px" 
                }}>
                  📱 소셜운 분석
                </SectionTitle>
                
                {extendedFortuneResult.data.social_fortune && (
                  <div style={{ marginTop: "20px" }}>
                    {/* SNS 활동 적기 */}
                    <div style={{ 
                      background: "#f8f0ff", 
                      padding: "20px", 
                      borderRadius: "12px", 
                      margin: "15px 0",
                      border: "1px solid #d8b3ff"
                    }}>
                      <h4 style={{ color: "#9370DB", marginBottom: "15px" }}>
                        📆 SNS 활동 적기
                      </h4>
                      <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginBottom: "15px" }}>
                        {extendedFortuneResult.data.social_fortune.sns_active_months?.map((month: string, i: number) => (
                          <span key={i} style={{
                            background: "#9370DB",
                            color: "white",
                            padding: "6px 14px",
                            borderRadius: "15px",
                            fontSize: "14px"
                          }}>
                            {month}
                          </span>
                        ))}
                      </div>
                      <p style={{ fontSize: "14px", color: "#666", fontStyle: "italic" }}>
                        {extendedFortuneResult.data.social_fortune.activity_reason}
                      </p>
                    </div>

                    {/* 프로필 색상 */}
                    <div style={{ 
                      background: "#f8f0ff", 
                      padding: "20px", 
                      borderRadius: "12px", 
                      margin: "15px 0",
                      border: "1px solid #d8b3ff"
                    }}>
                      <h4 style={{ color: "#9370DB", marginBottom: "15px" }}>
                        🎨 프로필 색상
                      </h4>
                      <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                        {extendedFortuneResult.data.social_fortune.profile_colors?.map((color: string, i: number) => (
                          <span key={i} style={{
                            background: "#9370DB",
                            color: "white",
                            padding: "8px 16px",
                            borderRadius: "20px",
                            fontSize: "14px",
                            fontWeight: "bold"
                          }}>
                            {color}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* 소통 스타일 */}
                    <div style={{ 
                      background: "#f8f0ff", 
                      padding: "20px", 
                      borderRadius: "12px", 
                      margin: "15px 0",
                      border: "1px solid #d8b3ff"
                    }}>
                      <h4 style={{ color: "#9370DB", marginBottom: "15px" }}>
                        💬 소통 스타일
                      </h4>
                      <p style={{ fontSize: "16px", color: "#495057", lineHeight: "1.6" }}>
                        {extendedFortuneResult.data.social_fortune.communication_style}
                      </p>
                    </div>

                    {/* 온라인 활동 시간 */}
                    <div style={{ 
                      background: "#f8f0ff", 
                      padding: "20px", 
                      borderRadius: "12px", 
                      margin: "15px 0",
                      border: "1px solid #d8b3ff"
                    }}>
                      <h4 style={{ color: "#9370DB", marginBottom: "15px" }}>
                        ⏰ 활동 시간
                      </h4>
                      <p style={{ fontSize: "16px", color: "#495057", lineHeight: "1.6" }}>
                        <strong>{extendedFortuneResult.data.social_fortune.online_timing}</strong>
                      </p>
                      <p style={{ fontSize: "14px", color: "#666", fontStyle: "italic", marginTop: "8px" }}>
                        {extendedFortuneResult.data.social_fortune.timing_reason}
                      </p>
                    </div>

                    {/* 소셜 전략 */}
                    <div style={{ 
                      background: "#f8f0ff", 
                      padding: "20px", 
                      borderRadius: "12px", 
                      margin: "15px 0",
                      border: "1px solid #d8b3ff"
                    }}>
                      <h4 style={{ color: "#9370DB", marginBottom: "15px" }}>
                        📈 소셜 전략
                      </h4>
                      <ul style={{ paddingLeft: "20px", color: "#495057" }}>
                        {extendedFortuneResult.data.social_fortune.social_strategies?.map((strategy: string, i: number) => (
                          <li key={i} style={{ marginBottom: "8px", lineHeight: "1.5" }}>
                            {strategy}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </AnalysisSection>

              {/* 취미운 분석 */}
              <AnalysisSection style={{ marginBottom: "25px" }}>
                <SectionTitle style={{ 
                  color: "#FF8C00", 
                  borderBottom: "3px solid #FF8C00", 
                  paddingBottom: "10px" 
                }}>
                  🎨 취미운 분석
                </SectionTitle>
                
                {extendedFortuneResult.data.hobby_fortune && (
                  <div style={{ marginTop: "20px" }}>
                    {/* 예술 분야 */}
                    <div style={{ 
                      background: "#fff8e1", 
                      padding: "20px", 
                      borderRadius: "12px", 
                      margin: "15px 0",
                      border: "1px solid #ffcc02"
                    }}>
                      <h4 style={{ color: "#FF8C00", marginBottom: "15px" }}>
                        🎭 예술 분야
                      </h4>
                      <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                        {extendedFortuneResult.data.hobby_fortune.art_fields?.map((field: string, i: number) => (
                          <span key={i} style={{
                            background: "#FF8C00",
                            color: "white",
                            padding: "8px 16px",
                            borderRadius: "20px",
                            fontSize: "14px",
                            fontWeight: "bold"
                          }}>
                            {field}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* 운동 종류 */}
                    <div style={{ 
                      background: "#fff8e1", 
                      padding: "20px", 
                      borderRadius: "12px", 
                      margin: "15px 0",
                      border: "1px solid #ffcc02"
                    }}>
                      <h4 style={{ color: "#FF8C00", marginBottom: "15px" }}>
                        🏃‍♂️ 운동 추천
                      </h4>
                      <p style={{ fontSize: "16px", color: "#495057", lineHeight: "1.6", marginBottom: "15px" }}>
                        <strong>{extendedFortuneResult.data.hobby_fortune.sports_type}</strong>
                      </p>
                      <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginBottom: "10px" }}>
                        {extendedFortuneResult.data.hobby_fortune.sports_examples?.map((sport: string, i: number) => (
                          <span key={i} style={{
                            background: "#28a745",
                            color: "white",
                            padding: "6px 12px",
                            borderRadius: "15px",
                            fontSize: "14px"
                          }}>
                            {sport}
                          </span>
                        ))}
                      </div>
                      <p style={{ fontSize: "14px", color: "#666", fontStyle: "italic" }}>
                        {extendedFortuneResult.data.hobby_fortune.sports_reason}
                      </p>
                    </div>

                    {/* 수집 취미 */}
                    <div style={{ 
                      background: "#fff8e1", 
                      padding: "20px", 
                      borderRadius: "12px", 
                      margin: "15px 0",
                      border: "1px solid #ffcc02"
                    }}>
                      <h4 style={{ color: "#FF8C00", marginBottom: "15px" }}>
                        🏺 수집 취미
                      </h4>
                      <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                        {extendedFortuneResult.data.hobby_fortune.collection_items?.map((item: string, i: number) => (
                          <span key={i} style={{
                            background: "#6f42c1",
                            color: "white",
                            padding: "6px 12px",
                            borderRadius: "15px",
                            fontSize: "14px"
                          }}>
                            {item}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* 창작 활동 */}
                    <div style={{ 
                      background: "#fff8e1", 
                      padding: "20px", 
                      borderRadius: "12px", 
                      margin: "15px 0",
                      border: "1px solid #ffcc02"
                    }}>
                      <h4 style={{ color: "#FF8C00", marginBottom: "15px" }}>
                        ✨ 창작 활동
                      </h4>
                      <ul style={{ paddingLeft: "20px", color: "#495057" }}>
                        {extendedFortuneResult.data.hobby_fortune.creative_activities?.map((activity: string, i: number) => (
                          <li key={i} style={{ marginBottom: "8px", lineHeight: "1.5" }}>
                            {activity}
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* 취미 조언 */}
                    <div style={{ 
                      background: "#fff8e1", 
                      padding: "20px", 
                      borderRadius: "12px", 
                      margin: "15px 0",
                      border: "1px solid #ffcc02"
                    }}>
                      <h4 style={{ color: "#FF8C00", marginBottom: "15px" }}>
                        💡 취미 조언
                      </h4>
                      <ul style={{ paddingLeft: "20px", color: "#495057" }}>
                        {extendedFortuneResult.data.hobby_fortune.hobby_advice?.map((advice: string, i: number) => (
                          <li key={i} style={{ marginBottom: "8px", lineHeight: "1.5" }}>
                            {advice}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </AnalysisSection>

            </div>
          ) : (
            <div style={{ padding: "15px", background: "#fff3cd", borderRadius: "8px" }}>
              <p style={{ color: "#856404", margin: 0 }}>
                확장 운세 분석 중 문제가 발생했습니다. 백엔드 API 연결을 확인해주세요.
              </p>
            </div>
          )}
        </ResultContainer>
      )}

      {/* 대운 분석 결과 */}
      {daeunResult && (
        <ResultContainer style={{ marginTop: "30px" }}>
          <SectionTitle
            style={{
              textAlign: "center",
              fontSize: "2rem",
              marginBottom: "30px",
            }}
          >
            🌟 대운 분석 결과
          </SectionTitle>

          {/* 기본 정보 */}
          <AnalysisSection>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: "20px",
              }}
            >
              <div>
                <strong>현재 나이:</strong>{" "}
                {daeunResult.daeun_analysis.current_age}세
              </div>
              <div>
                <strong>대운 시작:</strong>{" "}
                {daeunResult.daeun_analysis.daeun_start_age}세부터
              </div>
              <div>
                <strong>진행 방향:</strong>{" "}
                {daeunResult.daeun_analysis.is_forward ? "순행" : "역행"}
              </div>
            </div>
          </AnalysisSection>

          {/* 대운 타임라인 */}
          <AnalysisSection>
            <SectionTitle>📅 인생 대운 타임라인</SectionTitle>
            <div style={{ display: "grid", gap: "20px" }}>
              {daeunResult.daeun_analysis.daeun_list.map((daeun, index) => (
                <div
                  key={index}
                  style={{
                    border: daeun.is_current
                      ? "3px solid #f093fb"
                      : "2px solid #e9ecef",
                    borderRadius: "15px",
                    padding: "20px",
                    background: daeun.is_current
                      ? "linear-gradient(135deg, #f093fb22, #f5576c22)"
                      : "white",
                    boxShadow: daeun.is_current
                      ? "0 5px 20px rgba(240, 147, 251, 0.3)"
                      : "0 2px 10px rgba(0,0,0,0.1)",
                    position: "relative",
                  }}
                >
                  {daeun.is_current && (
                    <div
                      style={{
                        position: "absolute",
                        top: "-10px",
                        right: "20px",
                        background: "#f093fb",
                        color: "white",
                        padding: "5px 15px",
                        borderRadius: "20px",
                        fontSize: "12px",
                        fontWeight: "bold",
                      }}
                    >
                      현재 대운
                    </div>
                  )}

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "15px",
                    }}
                  >
                    <div>
                      <h4 style={{ margin: 0, color: "#2c3e50" }}>
                        {daeun.period}
                      </h4>
                      <p
                        style={{
                          margin: "5px 0",
                          color: "#6c757d",
                          fontSize: "14px",
                        }}
                      >
                        {daeun.gan}
                        {daeun.ji} ({daeun.gan_wuxing}·{daeun.ji_wuxing})
                      </p>
                    </div>
                    <div
                      style={{
                        padding: "8px 16px",
                        borderRadius: "20px",
                        fontWeight: "bold",
                        fontSize: "14px",
                        background:
                          daeun.fortune_level === "대길"
                            ? "#28a745"
                            : daeun.fortune_level === "소길"
                            ? "#17a2b8"
                            : daeun.fortune_level === "평운"
                            ? "#ffc107"
                            : daeun.fortune_level === "소흉"
                            ? "#fd7e14"
                            : "#dc3545",
                        color: "white",
                      }}
                    >
                      {daeun.fortune_level}
                    </div>
                  </div>

                  <div style={{ marginBottom: "15px" }}>
                    <strong style={{ color: "#495057", fontSize: "14px" }}>
                      특징:
                    </strong>
                    <div
                      style={{
                        marginTop: "8px",
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "8px",
                      }}
                    >
                      {daeun.characteristics.map((char, i) => (
                        <span
                          key={i}
                          style={{
                            background: "#e9ecef",
                            padding: "4px 12px",
                            borderRadius: "15px",
                            fontSize: "13px",
                            color: "#495057",
                          }}
                        >
                          {char}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div style={{ marginBottom: "15px" }}>
                    <strong style={{ color: "#495057", fontSize: "14px" }}>
                      주요 이벤트:
                    </strong>
                    <ul style={{ margin: "8px 0", paddingLeft: "20px" }}>
                      {daeun.major_events.map((event, i) => (
                        <li
                          key={i}
                          style={{
                            fontSize: "13px",
                            color: "#6c757d",
                            marginBottom: "4px",
                          }}
                        >
                          {event}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div
                    style={{
                      background: "#f8f9fa",
                      padding: "12px",
                      borderRadius: "8px",
                      borderLeft: "4px solid #17a2b8",
                    }}
                  >
                    <strong style={{ color: "#495057", fontSize: "14px" }}>
                      조언:
                    </strong>
                    <p
                      style={{
                        margin: "8px 0 0 0",
                        fontSize: "13px",
                        color: "#6c757d",
                        lineHeight: "1.5",
                      }}
                    >
                      {daeun.advice}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </AnalysisSection>
        </ResultContainer>
      )}

      {/* 세운 분석 결과 */}
      {saeunResult && (
        <ResultContainer style={{ marginTop: "30px" }}>
          <SectionTitle
            style={{
              textAlign: "center",
              fontSize: "2rem",
              marginBottom: "30px",
            }}
          >
            ⏰ {saeunResult.basic_info.target_year}년 세운 분석 결과
          </SectionTitle>

          {/* 연간 세운 정보 */}
          <AnalysisSection>
            <SectionTitle>
              📅 {saeunResult.saeun_analysis.target_year}년 연간 세운
            </SectionTitle>
            <div
              style={{
                background: "linear-gradient(135deg, #4caf50, #2e7d32)",
                color: "white",
                padding: "20px",
                borderRadius: "15px",
                textAlign: "center",
                marginBottom: "20px",
              }}
            >
              <h3 style={{ margin: "0 0 10px 0" }}>
                {saeunResult.saeun_analysis.yearly_saeun.gan}
                {saeunResult.saeun_analysis.yearly_saeun.ji}
              </h3>
              <p style={{ margin: "5px 0", fontSize: "14px", opacity: "0.9" }}>
                ({saeunResult.saeun_analysis.yearly_saeun.gan_wuxing}·
                {saeunResult.saeun_analysis.yearly_saeun.ji_wuxing})
              </p>
              <p style={{ margin: 0, lineHeight: "1.5" }}>
                {saeunResult.saeun_analysis.yearly_saeun.description}
              </p>
            </div>
          </AnalysisSection>

          {/* 전체 운세 요약 */}
          <AnalysisSection>
            <SectionTitle>🎯 종합 운세 점수</SectionTitle>
            <BalanceScore>
              <ScoreCircle
                $score={saeunResult.saeun_analysis.annual_score.normalized_score}
              >
                {saeunResult.saeun_analysis.annual_score.normalized_score}점
              </ScoreCircle>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "20px",
                  marginTop: "20px",
                }}
              >
                <div>
                  <h5 style={{ color: "#27ae60", marginBottom: "10px" }}>
                    🍀 최고의 달
                  </h5>
                  <div
                    style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}
                  >
                    {saeunResult.saeun_analysis.critical_periods.best_months.map(
                      (monthData, index) => (
                        <span
                          key={index}
                          style={{
                            background: "#27ae60",
                            color: "white",
                            padding: "4px 12px",
                            borderRadius: "15px",
                            fontSize: "14px",
                          }}
                        >
                          {monthData.month}월 ({monthData.level})
                        </span>
                      )
                    )}
                  </div>
                </div>

                <div>
                  <h5 style={{ color: "#e74c3c", marginBottom: "10px" }}>
                    ⚠️ 주의할 달
                  </h5>
                  <div
                    style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}
                  >
                    {saeunResult.saeun_analysis.critical_periods.caution_months.map(
                      (monthData, index) => (
                        <span
                          key={index}
                          style={{
                            background: "#e74c3c",
                            color: "white",
                            padding: "4px 12px",
                            borderRadius: "15px",
                            fontSize: "14px",
                          }}
                        >
                          {monthData.month}월 ({monthData.level})
                        </span>
                      )
                    )}
                  </div>
                </div>
              </div>
            </BalanceScore>
          </AnalysisSection>

          {/* 월별 세운 상세 */}
          <AnalysisSection>
            <SectionTitle>📊 월별 운세 상세 분석</SectionTitle>
            <div style={{ display: "grid", gap: "20px" }}>
              {saeunResult.saeun_analysis.monthly_saeun.map(
                (monthData, index) => {
                  const interactionData =
                    saeunResult.saeun_analysis.saeun_interaction.monthly.find(
                      (m) => m.month === monthData.month
                    );
                  return (
                    <div
                      key={index}
                      style={{
                        border: "2px solid #e9ecef",
                        borderRadius: "15px",
                        padding: "20px",
                        background: "white",
                        boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
                        borderColor:
                          interactionData?.fortune_level === "대길"
                            ? "#28a745"
                            : interactionData?.fortune_level === "소길"
                            ? "#17a2b8"
                            : interactionData?.fortune_level === "평운"
                            ? "#ffc107"
                            : interactionData?.fortune_level === "소흉"
                            ? "#fd7e14"
                            : "#dc3545",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: "15px",
                        }}
                      >
                        <div>
                          <h4 style={{ margin: 0, color: "#2c3e50" }}>
                            {monthData.month}월
                          </h4>
                          <p
                            style={{
                              margin: "5px 0",
                              color: "#6c757d",
                              fontSize: "14px",
                            }}
                          >
                            {monthData.gan}
                            {monthData.ji} ({monthData.gan_wuxing}·
                            {monthData.ji_wuxing})
                          </p>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "15px",
                          }}
                        >
                          <div
                            style={{
                              padding: "8px 16px",
                              borderRadius: "20px",
                              fontWeight: "bold",
                              fontSize: "14px",
                              background:
                                interactionData?.fortune_level === "대길"
                                  ? "#28a745"
                                  : interactionData?.fortune_level === "소길"
                                  ? "#17a2b8"
                                  : interactionData?.fortune_level === "평운"
                                  ? "#ffc107"
                                  : interactionData?.fortune_level === "소흉"
                                  ? "#fd7e14"
                                  : "#dc3545",
                              color: "white",
                            }}
                          >
                            {interactionData?.fortune_level || "평운"}
                          </div>
                          <div
                            style={{
                              fontSize: "18px",
                              fontWeight: "bold",
                              color: "#2c3e50",
                            }}
                          >
                            {interactionData?.score || 0}점
                          </div>
                        </div>
                      </div>

                      <div style={{ marginBottom: "15px" }}>
                        <p
                          style={{
                            margin: 0,
                            fontSize: "15px",
                            lineHeight: "1.5",
                            color: "#495057",
                          }}
                        >
                          {interactionData?.characteristics?.join(", ") ||
                            "평상시와 같은 운세입니다"}
                        </p>
                      </div>

                      {interactionData &&
                        (interactionData.opportunities.length > 0 ||
                          interactionData.warnings.length > 0) && (
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: "1fr 1fr",
                              gap: "15px",
                              marginTop: "15px",
                            }}
                          >
                            {interactionData.opportunities.length > 0 && (
                              <div>
                                <strong
                                  style={{ color: "#28a745", fontSize: "13px" }}
                                >
                                  🍀 기회:
                                </strong>
                                <ul
                                  style={{
                                    margin: "5px 0",
                                    paddingLeft: "15px",
                                    fontSize: "12px",
                                  }}
                                >
                                  {interactionData.opportunities.map(
                                    (opportunity, i) => (
                                      <li
                                        key={i}
                                        style={{
                                          color: "#495057",
                                          marginBottom: "2px",
                                        }}
                                      >
                                        {opportunity}
                                      </li>
                                    )
                                  )}
                                </ul>
                              </div>
                            )}

                            {interactionData.warnings.length > 0 && (
                              <div>
                                <strong
                                  style={{ color: "#dc3545", fontSize: "13px" }}
                                >
                                  ⚠️ 주의사항:
                                </strong>
                                <ul
                                  style={{
                                    margin: "5px 0",
                                    paddingLeft: "15px",
                                    fontSize: "12px",
                                  }}
                                >
                                  {interactionData.warnings.map(
                                    (warning, i) => (
                                      <li
                                        key={i}
                                        style={{
                                          color: "#495057",
                                          marginBottom: "2px",
                                        }}
                                      >
                                        {warning}
                                      </li>
                                    )
                                  )}
                                </ul>
                              </div>
                            )}
                          </div>
                        )}
                    </div>
                  );
                }
              )}
            </div>
          </AnalysisSection>

          {/* 연간 조언 */}
          <AnalysisSection>
            <SectionTitle>
              💡 {saeunResult.saeun_analysis.target_year}년 종합 조언
            </SectionTitle>
            <div
              style={{
                background: "#f8f9fa",
                padding: "20px",
                borderRadius: "10px",
                borderLeft: "5px solid #4caf50",
              }}
            >
              <p
                style={{
                  margin: 0,
                  fontSize: "16px",
                  lineHeight: "1.6",
                  color: "#495057",
                }}
              >
                {saeunResult.saeun_analysis.summary}
              </p>
            </div>
          </AnalysisSection>


          {/* Phase 2 확장 운세 분석 결과 */}
          {phase2FortuneResult && (
            <div style={{ marginTop: "30px" }}>
              <SectionTitle
                style={{
                  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  color: "white",
                  textAlign: "center"
                }}
              >
                🔮 Phase 2 확장 운세 분석
              </SectionTitle>

              {phase2FortuneResult.success ? (
                <div>
                  {/* 직업운 분석 */}
                  <AnalysisSection style={{ marginBottom: "25px" }}>
                    <SectionTitle
                      style={{
                        background: "#6f42c1",
                        color: "white",
                        borderRadius: "15px 15px 0 0"
                      }}
                    >
                      💼 직업운 상세 분석
                    </SectionTitle>
                    
                    {phase2FortuneResult.data.phase2_fortune?.career_fortune && (
                      <div style={{ marginTop: "20px" }}>
                        {/* 이직 시기 */}
                        <div style={{ 
                          background: "#f8f9fa", 
                          padding: "15px", 
                          borderRadius: "10px", 
                          marginBottom: "15px",
                          borderLeft: "5px solid #6f42c1"
                        }}>
                          <h4 style={{ margin: "0 0 10px 0", color: "#2c3e50" }}>
                            📅 이직 적기
                          </h4>
                          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginBottom: "10px" }}>
                            {phase2FortuneResult.data.phase2_fortune.career_fortune.job_change_months?.map((month: string, i: number) => (
                              <span key={i} style={{
                                background: "#6f42c1",
                                color: "white",
                                padding: "5px 12px",
                                borderRadius: "15px",
                                fontSize: "12px",
                                fontWeight: "bold"
                              }}>
                                {month}
                              </span>
                            ))}
                          </div>
                          <p style={{ fontSize: "14px", color: "#666", fontStyle: "italic", margin: 0 }}>
                            {phase2FortuneResult.data.phase2_fortune.career_fortune.change_reason}
                          </p>
                        </div>

                        {/* 승진운 */}
                        <div style={{ 
                          background: "#e8f5e8", 
                          padding: "15px", 
                          borderRadius: "10px", 
                          marginBottom: "15px",
                          borderLeft: "5px solid #28a745"
                        }}>
                          <h4 style={{ margin: "0 0 10px 0", color: "#2c3e50" }}>
                            🚀 승진운
                          </h4>
                          <p style={{ fontSize: "16px", color: "#495057", lineHeight: "1.6", margin: 0 }}>
                            {phase2FortuneResult.data.phase2_fortune.career_fortune.promotion_advice}
                          </p>
                        </div>

                        {/* 창업/부업 */}
                        <div style={{ 
                          background: "#fff3cd", 
                          padding: "15px", 
                          borderRadius: "10px", 
                          marginBottom: "15px",
                          borderLeft: "5px solid #ffc107"
                        }}>
                          <h4 style={{ margin: "0 0 10px 0", color: "#2c3e50" }}>
                            💡 창업/부업 분야
                          </h4>
                          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginBottom: "10px" }}>
                            {phase2FortuneResult.data.phase2_fortune.career_fortune.startup_fields?.map((field: string, i: number) => (
                              <span key={i} style={{
                                background: "#ffc107",
                                color: "#212529",
                                padding: "5px 12px",
                                borderRadius: "15px",
                                fontSize: "12px",
                                fontWeight: "bold"
                              }}>
                                {field}
                              </span>
                            ))}
                          </div>
                          <h5 style={{ margin: "15px 0 5px 0", color: "#2c3e50" }}>추천 부업:</h5>
                          <ul style={{ paddingLeft: "20px", color: "#495057", margin: 0 }}>
                            {phase2FortuneResult.data.phase2_fortune.career_fortune.side_jobs?.map((job: string, i: number) => (
                              <li key={i} style={{ marginBottom: "5px" }}>{job}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    )}
                  </AnalysisSection>

                  {/* 건강운 분석 */}
                  <AnalysisSection style={{ marginBottom: "25px" }}>
                    <SectionTitle
                      style={{
                        background: "#dc3545",
                        color: "white",
                        borderRadius: "15px 15px 0 0"
                      }}
                    >
                      🏥 건강운 세분화
                    </SectionTitle>
                    
                    {phase2FortuneResult.data.phase2_fortune?.health_fortune && (
                      <div style={{ marginTop: "20px" }}>
                        {/* 장기별 건강 관리 */}
                        <div style={{ 
                          background: "#f8f9fa", 
                          padding: "15px", 
                          borderRadius: "10px", 
                          marginBottom: "15px",
                          borderLeft: "5px solid #dc3545"
                        }}>
                          <h4 style={{ margin: "0 0 15px 0", color: "#2c3e50" }}>
                            🫁 장기별 건강 관리
                          </h4>
                          {Object.entries(phase2FortuneResult.data.phase2_fortune.health_fortune.organ_care || {}).map(([organ, advice], i) => (
                            <div key={i} style={{ marginBottom: "10px" }}>
                              <strong style={{ color: "#dc3545" }}>{organ}:</strong>
                              <span style={{ marginLeft: "8px", color: "#495057" }}>{advice as string}</span>
                            </div>
                          ))}
                        </div>

                        {/* 운동 추천 */}
                        <div style={{ 
                          background: "#e8f4fd", 
                          padding: "15px", 
                          borderRadius: "10px", 
                          marginBottom: "15px",
                          borderLeft: "5px solid #17a2b8"
                        }}>
                          <h4 style={{ margin: "0 0 10px 0", color: "#2c3e50" }}>
                            🏃‍♂️ 추천 운동
                          </h4>
                          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                            {phase2FortuneResult.data.phase2_fortune.health_fortune.exercise_recommendations?.map((exercise: string, i: number) => (
                              <span key={i} style={{
                                background: "#17a2b8",
                                color: "white",
                                padding: "5px 12px",
                                borderRadius: "15px",
                                fontSize: "12px",
                                fontWeight: "bold"
                              }}>
                                {exercise}
                              </span>
                            ))}
                          </div>
                        </div>

                        {/* 식단 조언 */}
                        <div style={{ 
                          background: "#d1ecf1", 
                          padding: "15px", 
                          borderRadius: "10px", 
                          marginBottom: "15px",
                          borderLeft: "5px solid #0dcaf0"
                        }}>
                          <h4 style={{ margin: "0 0 10px 0", color: "#2c3e50" }}>
                            🍎 식단 조언
                          </h4>
                          <ul style={{ paddingLeft: "20px", color: "#495057", margin: 0 }}>
                            {phase2FortuneResult.data.phase2_fortune.health_fortune.diet_advice?.map((advice: string, i: number) => (
                              <li key={i} style={{ marginBottom: "5px" }}>{advice}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    )}
                  </AnalysisSection>

                  {/* 학업/자기계발운 분석 */}
                  <AnalysisSection style={{ marginBottom: "25px" }}>
                    <SectionTitle
                      style={{
                        background: "#20c997",
                        color: "white",
                        borderRadius: "15px 15px 0 0"
                      }}
                    >
                      📚 학업/자기계발운
                    </SectionTitle>
                    
                    {phase2FortuneResult.data.phase2_fortune?.study_fortune && (
                      <div style={{ marginTop: "20px" }}>
                        {/* 학습 적기 */}
                        <div style={{ 
                          background: "#f8f9fa", 
                          padding: "15px", 
                          borderRadius: "10px", 
                          marginBottom: "15px",
                          borderLeft: "5px solid #20c997"
                        }}>
                          <h4 style={{ margin: "0 0 10px 0", color: "#2c3e50" }}>
                            📅 학습 적기
                          </h4>
                          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginBottom: "10px" }}>
                            {phase2FortuneResult.data.phase2_fortune.study_fortune.study_months?.map((month: string, i: number) => (
                              <span key={i} style={{
                                background: "#20c997",
                                color: "white",
                                padding: "5px 12px",
                                borderRadius: "15px",
                                fontSize: "12px",
                                fontWeight: "bold"
                              }}>
                                {month}
                              </span>
                            ))}
                          </div>
                          <p style={{ fontSize: "14px", color: "#666", fontStyle: "italic", margin: 0 }}>
                            {phase2FortuneResult.data.phase2_fortune.study_fortune.study_reason}
                          </p>
                        </div>

                        {/* 자격증 추천 */}
                        <div style={{ 
                          background: "#e2e3e5", 
                          padding: "15px", 
                          borderRadius: "10px", 
                          marginBottom: "15px",
                          borderLeft: "5px solid #6c757d"
                        }}>
                          <h4 style={{ margin: "0 0 10px 0", color: "#2c3e50" }}>
                            🏆 추천 자격증
                          </h4>
                          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                            {phase2FortuneResult.data.phase2_fortune.study_fortune.certifications?.map((cert: string, i: number) => (
                              <span key={i} style={{
                                background: "#6c757d",
                                color: "white",
                                padding: "5px 12px",
                                borderRadius: "15px",
                                fontSize: "12px",
                                fontWeight: "bold"
                              }}>
                                {cert}
                              </span>
                            ))}
                          </div>
                        </div>

                        {/* 독서 장르 */}
                        <div style={{ 
                          background: "#fff3cd", 
                          padding: "15px", 
                          borderRadius: "10px", 
                          marginBottom: "15px",
                          borderLeft: "5px solid #ffc107"
                        }}>
                          <h4 style={{ margin: "0 0 10px 0", color: "#2c3e50" }}>
                            📖 추천 독서 장르
                          </h4>
                          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                            {phase2FortuneResult.data.phase2_fortune.study_fortune.reading_genres?.map((genre: string, i: number) => (
                              <span key={i} style={{
                                background: "#ffc107",
                                color: "#212529",
                                padding: "5px 12px",
                                borderRadius: "15px",
                                fontSize: "12px",
                                fontWeight: "bold"
                              }}>
                                {genre}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </AnalysisSection>

                  {/* 가족운 분석 */}
                  <AnalysisSection style={{ marginBottom: "25px" }}>
                    <SectionTitle
                      style={{
                        background: "#fd7e14",
                        color: "white",
                        borderRadius: "15px 15px 0 0"
                      }}
                    >
                      👨‍👩‍👧‍👦 가족운
                    </SectionTitle>
                    
                    {phase2FortuneResult.data.phase2_fortune?.family_fortune && (
                      <div style={{ marginTop: "20px" }}>
                        {/* 부모 관계 */}
                        <div style={{ 
                          background: "#f8f9fa", 
                          padding: "15px", 
                          borderRadius: "10px", 
                          marginBottom: "15px",
                          borderLeft: "5px solid #fd7e14"
                        }}>
                          <h4 style={{ margin: "0 0 10px 0", color: "#2c3e50" }}>
                            👴👵 부모님과의 관계
                          </h4>
                          <p style={{ fontSize: "16px", color: "#495057", lineHeight: "1.6", marginBottom: "10px" }}>
                            {phase2FortuneResult.data.phase2_fortune.family_fortune.parent_relationship}
                          </p>
                          <ul style={{ paddingLeft: "20px", color: "#495057", margin: 0 }}>
                            {phase2FortuneResult.data.phase2_fortune.family_fortune.parent_advice?.map((advice: string, i: number) => (
                              <li key={i} style={{ marginBottom: "5px" }}>{advice}</li>
                            ))}
                          </ul>
                        </div>

                        {/* 자녀 계획 */}
                        <div style={{ 
                          background: "#d1ecf1", 
                          padding: "15px", 
                          borderRadius: "10px", 
                          marginBottom: "15px",
                          borderLeft: "5px solid #0dcaf0"
                        }}>
                          <h4 style={{ margin: "0 0 10px 0", color: "#2c3e50" }}>
                            👶 자녀 계획
                          </h4>
                          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                            {phase2FortuneResult.data.phase2_fortune.family_fortune.children_timing?.map((timing: string, i: number) => (
                              <span key={i} style={{
                                background: "#0dcaf0",
                                color: "white",
                                padding: "5px 12px",
                                borderRadius: "15px",
                                fontSize: "12px",
                                fontWeight: "bold"
                              }}>
                                {timing}
                              </span>
                            ))}
                          </div>
                        </div>

                        {/* 가족 모임 */}
                        <div style={{ 
                          background: "#e2e3e5", 
                          padding: "15px", 
                          borderRadius: "10px", 
                          marginBottom: "15px",
                          borderLeft: "5px solid #6c757d"
                        }}>
                          <h4 style={{ margin: "0 0 10px 0", color: "#2c3e50" }}>
                            🎉 가족 모임 활동
                          </h4>
                          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                            {phase2FortuneResult.data.phase2_fortune.family_fortune.gathering_activities?.map((activity: string, i: number) => (
                              <span key={i} style={{
                                background: "#6c757d",
                                color: "white",
                                padding: "5px 12px",
                                borderRadius: "15px",
                                fontSize: "12px",
                                fontWeight: "bold"
                              }}>
                                {activity}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </AnalysisSection>
                </div>
              ) : (
                <div style={{
                  background: "#f8d7da",
                  color: "#721c24",
                  padding: "20px",
                  borderRadius: "10px",
                  textAlign: "center"
                }}>
                  <p style={{ margin: 0, fontSize: "16px" }}>
                    Phase 2 확장 운세 분석 중 문제가 발생했습니다. 백엔드 API 연결을 확인해주세요.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* AI 채팅 버튼들 */}
          <AIButtonContainer>
            <AIButton onClick={() => setShowAIChat(true)}>
              🤖 Gemini AI 해석
            </AIButton>
            <AzureButton onClick={() => setShowAzureChat(true)}>
              ⚡ Azure GPT-4.1 해석
            </AzureButton>
          </AIButtonContainer>
        </ResultContainer>
      )}

      {/* AI 채팅 인터페이스들 */}
      {result && (
        <>
          <AIChatInterface
            birthInfo={{
              year: parseInt(formData.year),
              month: parseInt(formData.month),
              day: parseInt(formData.day),
              hour: parseInt(formData.hour),
              gender: formData.gender,
              name: formData.name
            }}
            isVisible={showAIChat}
            onClose={() => setShowAIChat(false)}
          />
          <AzureAIChatInterface
            birthInfo={{
              year: parseInt(formData.year),
              month: parseInt(formData.month),
              day: parseInt(formData.day),
              hour: parseInt(formData.hour),
              gender: formData.gender,
              name: formData.name
            }}
            isVisible={showAzureChat}
            onClose={() => setShowAzureChat(false)}
          />
        </>
      )}
    </Container>
  );
}


function Root() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/client" element={<ClientPage />} />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/report/:id" element={<ReportPage />} />
        <Route path="/payment/success" element={<PaymentSuccessPage />} />
        <Route path="/payment/fail" element={<PaymentFailPage />} />
        <Route path="/legal/:type" element={<LegalPage />} />
        <Route path="/visit" element={<VisitPage />} />
        <Route path="/" element={<MainPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default Root;
